import { ValueOf } from '../types';

type MessageGetter = (...args: any[]) => string;
type MessageOrGetter = string | MessageGetter;

type ErrorContext = Partial<{
  details: string;
  originalError: Error;
}>;

type ErrorContextArg<MG extends MessageOrGetter> = MG extends MessageGetter
  ? ErrorContext & { messageArgs: Parameters<MG> }
  : ErrorContext | void;

export const createError = <
  const E extends string,
  R extends Record<string, string>,
  MG extends MessageOrGetter,
>(
  errorName: E,
  messageOrGetter: MG,
  reasons: R
) =>
  ({
    [errorName]: class extends Error {
      public readonly details?: string;
      public readonly originalError?: Error;
      public readonly cause?: Error;
      public readonly reason: ValueOf<R>;
      static Reason = reasons;

      constructor(reason: ValueOf<R>, context: ErrorContextArg<MG>) {
        const messageArgs =
          context && 'messageArgs' in context ? context.messageArgs : [];
        const { details, originalError } = context || {};
        const message =
          typeof messageOrGetter === 'function'
            ? messageOrGetter(...messageArgs)
            : messageOrGetter;

        super(`${message} (Reason: ${reason})`);

        this.reason = reason;
        if (originalError instanceof Error) {
          this.originalError = originalError;
          this.cause = originalError;
          this.details = originalError.message;
        }
        if (typeof details === 'string') {
          this.details = details;
        }
      }
    },
  })[errorName];
